import lien from '../assets/lien.png';
import { type CategoryPublication, type CategoryPublicationMediaSyncItem } from '../api/category';
import {
    isJpgMediaExtension,
    type Media,
    mediaUrlExtension,
    normalizeMediaExtension,
} from '../api/media';
import { type MediaCarousel } from '../publications/form/PublicationForm';
import { resolvePreviewUrl } from '../service/previewUrl';
import { Option } from '../types/option';

let carouselLocalIdCounter = 0;

export function createCarouselLocalId(): string {
    carouselLocalIdCounter += 1;
    return `carousel-${carouselLocalIdCounter}`;
}

export function sortPublicationMediasByPriority(medias: ReadonlyArray<Media>): Media[] {
    return [...medias].sort((a, b) => a.priority - b.priority);
}

export function nextUploadSequenceFromMedias(medias: ReadonlyArray<Media>): number {
    if (medias.length === 0) {
        return 0;
    }

    return Math.max(...medias.map(media => media.priority)) + 1;
}

export function primaryMediaFromPublication(publication: CategoryPublication): Option<Media> {
    const sorted = sortPublicationMediasByPriority(publication.medias);
    if (sorted.length === 0) {
        return Option.none();
    }

    return Option.some(sorted[0]);
}

export type CategoryPublicationCarouselItem = {
    localId: string;
    url: string;
    fileName: string;
    order: number;
    networkKey: Option<string>;
    id: Option<number>;
    uploadSequence: Option<number>;
};

export function toMediaCarouselView(items: CategoryPublicationCarouselItem[]): MediaCarousel[] {
    return items.map(item => ({
        url: item.url,
        fileName: item.fileName,
        order: item.order,
        networkKey: item.networkKey.getOrElse(''),
        id: item.id.getOrElse(0),
        localId: item.localId,
    }));
}

export function carouselMediasForPublicationForm(
    publication: CategoryPublication,
    primaryExtension: string,
): MediaCarousel[] {
    if (!isJpgMediaExtension(primaryExtension)) {
        return [];
    }

    return toMediaCarouselView(appendEmptyCarouselSlot(carouselMediasFromPublication(publication)));
}

export function carouselMediasFromPublication(
    publication: CategoryPublication,
): CategoryPublicationCarouselItem[] {
    const sorted = sortPublicationMediasByPriority(publication.medias);

    return sorted.slice(1).map(media => ({
        localId: createCarouselLocalId(),
        url: resolvePreviewUrl(media.url, {
            extension: mediaUrlExtension(media.url),
        }),
        fileName: media.url,
        order: media.priority,
        networkKey: Option.none(),
        id: Option.some(media.id),
        uploadSequence: Option.some(media.priority),
    }));
}

export function nextUploadSequenceFromPublication(publication: CategoryPublication): number {
    return nextUploadSequenceFromMedias(publication.medias);
}

export function publicationHasLink(publication: CategoryPublication): boolean {
    return String(publication.link ?? '').trim() !== '';
}

export function primaryPreviewFromMedia(media: Media): { url: string; extension: string } {
    const extension = normalizeMediaExtension(mediaUrlExtension(media.url));

    return {
        url: resolvePreviewUrl(media.url, { extension }),
        extension,
    };
}

export function emptyCarouselSlot(): CategoryPublicationCarouselItem {
    return {
        localId: createCarouselLocalId(),
        url: '',
        fileName: '',
        order: 1,
        networkKey: Option.none(),
        id: Option.none(),
        uploadSequence: Option.none(),
    };
}

export function appendEmptyCarouselSlot(
    items: CategoryPublicationCarouselItem[],
): CategoryPublicationCarouselItem[] {
    if (items.length >= 9 || items.some(item => item.url === '')) {
        return items;
    }

    return [...items, { ...emptyCarouselSlot(), order: items.length + 1 }];
}

export function libraryPreviewUrl(publication: CategoryPublication, videoAsset: string): string {
    if (publicationHasLink(publication)) {
        return lien;
    }

    return primaryMediaFromPublication(publication)
        .filter(media => media.url.trim() !== '')
        .map(media => {
            const extension = mediaUrlExtension(media.url);
            return resolvePreviewUrl(media.url, { extension, videoAsset });
        })
        .getOrElse('');
}

export function hasCategoryPrimaryMediaPreview(
    media: string,
    extension: string,
    link: string,
): boolean {
    return Boolean(media) && media !== lien && Boolean(extension) && link.trim() === '';
}

export function reservesPrimaryMediaSlot(options: {
    existingPrimaryMedia: Option<Media>;
    selectedPrimaryFile: Option<File>;
    hasPrimaryMediaPreview: boolean;
    initialMedias: ReadonlyArray<Media>;
    carouselFiles: ReadonlyMap<string, File>;
}): boolean {
    if (options.selectedPrimaryFile.isDefined()) {
        return true;
    }

    if (!options.hasPrimaryMediaPreview) {
        return false;
    }

    if (!options.existingPrimaryMedia.isDefined()) {
        return false;
    }

    if (options.carouselFiles.size > 0 && options.initialMedias.length === 1) {
        return false;
    }

    return true;
}

export function computeRemovedCategoryPublicationMediaIds(options: {
    initialMedias: ReadonlyArray<Media>;
    existingPrimaryMedia: Option<Media>;
    selectedPrimaryFile: Option<File>;
    hasPrimaryMediaPreview: boolean;
    carouselItems: ReadonlyArray<CategoryPublicationCarouselItem>;
    carouselFiles: ReadonlyMap<string, File>;
}): number[] {
    const keepsPrimary = reservesPrimaryMediaSlot({
        existingPrimaryMedia: options.existingPrimaryMedia,
        selectedPrimaryFile: options.selectedPrimaryFile,
        hasPrimaryMediaPreview: options.hasPrimaryMediaPreview,
        initialMedias: options.initialMedias,
        carouselFiles: options.carouselFiles,
    });

    const keptMediaIds = new Set<number>([
        ...(keepsPrimary
            ? options.existingPrimaryMedia.fold(
                  () => [],
                  media => [media.id],
              )
            : []),
        ...options.carouselItems.flatMap(item =>
            item.id.fold(
                () => [],
                mediaId => [mediaId],
            ),
        ),
    ]);

    return options.initialMedias
        .map(media => media.id)
        .filter(mediaId => !keptMediaIds.has(mediaId));
}

export function buildPublicationMediaSyncItems(options: {
    existingPrimaryMedia: Option<Media>;
    selectedPrimaryFile: Option<File>;
    primaryUploadSequence: Option<number>;
    hasPrimaryMediaPreview: boolean;
    initialMedias: ReadonlyArray<Media>;
    carouselItems: ReadonlyArray<CategoryPublicationCarouselItem>;
    carouselFiles: ReadonlyMap<string, File>;
}): CategoryPublicationMediaSyncItem[] {
    const items: CategoryPublicationMediaSyncItem[] = [];
    const uploadsPrimary = options.selectedPrimaryFile.isDefined();
    const reservesPrimary = reservesPrimaryMediaSlot(options);
    const primaryPriority = options.existingPrimaryMedia.map(media => media.priority).getOrElse(0);

    if (uploadsPrimary) {
        items.push({
            id: options.existingPrimaryMedia.map(media => media.id),
            file: options.selectedPrimaryFile,
            uploadSequence: options.primaryUploadSequence.getOrElse(primaryPriority),
        });
    }

    options.carouselItems.forEach(item => {
        if (!item.url) {
            return;
        }

        const file = options.carouselFiles.get(item.localId);
        if (!file) {
            return;
        }

        const replacesPrimary = !reservesPrimary && options.existingPrimaryMedia.isDefined();

        const uploadSequence = item.uploadSequence.getOrElse(
            nextUploadSequenceFromMedias(options.initialMedias),
        );

        items.push({
            id: replacesPrimary ? options.existingPrimaryMedia.map(media => media.id) : item.id,
            file: Option.some(file),
            uploadSequence: replacesPrimary ? primaryPriority : uploadSequence,
        });
    });

    return items;
}

export function libraryPreviewExtension(publication: CategoryPublication): string {
    if (publicationHasLink(publication)) {
        return '';
    }

    const primary = primaryMediaFromPublication(publication);
    return primary
        .filter(media => media.url.trim() !== '')
        .map(media => mediaUrlExtension(media.url))
        .getOrElse('');
}

export function previewMediasForCategoryPublicationForm(options: {
    primaryPreviewUrl: string;
    extension: string;
    primaryPriority: number;
    carouselItems: ReadonlyArray<CategoryPublicationCarouselItem>;
}): Media[] {
    if (!options.primaryPreviewUrl || options.extension.toLowerCase() === 'mp4') {
        return [];
    }

    const carouselPreviewMedias = options.carouselItems
        .filter(item => item.url.trim() !== '')
        .map((item, index) => ({
            id: item.id.getOrElse(index + 1),
            url: item.url,
            priority: item.uploadSequence.getOrElse(item.order),
        }));

    return [
        {
            id: 0,
            url: options.primaryPreviewUrl,
            priority: options.primaryPriority,
        },
        ...carouselPreviewMedias,
    ];
}
